import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useViewAs, type ViewAsOverrides } from '@/contexts/ViewAsContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { PlanTier } from '@/lib/stripe';

const TIER_OPTIONS: { value: PlanTier; labelKey: string; color: string }[] = [
  { value: 'free', labelKey: 'viewAs.free', color: 'bg-muted text-muted-foreground' },
  { value: 'standard', labelKey: 'viewAs.standard', color: 'bg-blue-500/20 text-blue-700 dark:text-blue-300' },
  { value: 'premium', labelKey: 'viewAs.premium', color: 'bg-amber-500/20 text-amber-700 dark:text-amber-300' },
];

export const ViewAsToolbar = () => {
  const { isStaff } = useAuth();
  const { active, overrides, setOverrides, reset } = useViewAs();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  // Only show for staff
  if (!isStaff) return null;

  if (!expanded) {
    return (
      <div className="fixed bottom-4 right-4 z-[9999]">
        <Button
          size="sm"
          variant={active ? 'default' : 'outline'}
          onClick={() => setExpanded(true)}
          className="shadow-lg gap-1.5 rounded-full px-3"
        >
          {active ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {active ? t('viewAs.active', { defaultValue: 'View As' }) : t('viewAs.toggle', { defaultValue: 'View As' })}
          {active && (
            <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">
              {overrides.tier ?? 'custom'}
            </Badge>
          )}
          <ChevronUp className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  const handleTierChange = (tier: PlanTier) => {
    setOverrides({
      ...overrides,
      tier,
      simulateRegularUser: true,
    });
  };

  return (
    <div className="fixed bottom-4 right-4 z-[9999] bg-card border border-border rounded-xl shadow-2xl p-4 w-80 max-w-[calc(100vw-2rem)] space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">{t('viewAs.title', { defaultValue: 'View As User' })}</span>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setExpanded(false)}>
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Tier selector */}
      <div className="space-y-1.5">
        <p className="text-xs text-muted-foreground font-medium">
          {t('viewAs.selectTier', { defaultValue: 'Subscription Tier' })}
        </p>
        <div className="flex gap-1.5">
          {TIER_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              size="sm"
              variant={overrides.tier === opt.value ? 'default' : 'outline'}
              className="flex-1 text-xs h-8 min-w-0 truncate px-2"
              onClick={() => handleTierChange(opt.value)}
            >
              {t(opt.labelKey)}
            </Button>
          ))}
        </div>
      </div>

      {/* Status */}
      {active && (
        <div className="bg-warning/10 border border-warning/30 rounded-lg p-2">
          <p className="text-xs text-warning-foreground">
            ⚠ {t('viewAs.warning', { defaultValue: 'You are viewing the site as a simulated user. Server-side checks are NOT affected.' })}
          </p>
        </div>
      )}

      {/* Reset */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 text-xs"
          onClick={() => { reset(); setExpanded(false); }}
          disabled={!active}
        >
          <X className="h-3 w-3 mr-1" />
          {t('viewAs.reset', { defaultValue: 'Reset to Staff' })}
        </Button>
      </div>
    </div>
  );
};